import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'

export type { ZipLookupResponse } from '../services/cepLookup'

export interface Branch {
  id: string
  name: string
  phone?: string
  email?: string
  street: string
  number: string
  complement?: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  isActive: boolean
  isPrimary: boolean
  allowedVehicleCategories?: string[]
  stateRegistration?: string
  taxRegime?: string
  fiscalDefaultNcm?: string
  fiscalDefaultCfop?: string
  createdAt: string
}

export function useBranches() {
  const [branches, setBranches] = useState<Branch[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBranches = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const raw = await apiService.get<Branch[] | { data?: Branch[]; success?: boolean }>('/dealership/branches')
      let list: Branch[] = []
      if (Array.isArray(raw)) {
        list = raw
      } else if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: Branch[] }).data)) {
        list = (raw as { data: Branch[] }).data
      }
      setBranches(list)
    } catch (err: any) {
      setBranches([])
      if (err?.status !== 404) setError(err?.message || 'Erro ao carregar filiais')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createBranch = async (data: Omit<Branch, 'id' | 'createdAt'>) => {
    const result = await apiService.post<Branch>('/dealership/branches', data)
    await fetchBranches()
    return result
  }

  const updateBranch = async (id: string, data: Partial<Branch>) => {
    await apiService.put<Branch>(`/dealership/branches/${id}`, data)
    await fetchBranches()
  }

  const deleteBranch = async (id: string) => {
    await apiService.delete<void>(`/dealership/branches/${id}`)
    setBranches(prev => prev.filter(b => b.id !== id))
  }

  useEffect(() => { fetchBranches() }, [fetchBranches])

  return { branches, isLoading, error, createBranch, updateBranch, deleteBranch, fetchBranches }
}
